import React, { useEffect, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import Sidebar from 'react-sidebar';
import DashboardContrastIcon from '../../assets/images/svg/menu-dashboard-contrast-icon.svg';
import DashboardIcon from '../../assets/images/svg/menu-dashboard-icon.svg';
import { isAuthenticated } from '../../services/AuthService';
import { getElementID } from '../../utils/HTMLUtils';
import BarChartIcon from '../Icons/BarChartIcon';
import CalendarTodayIcon from '../Icons/CalendarTodayIcon';
import InsertDriveFileIcon from '../Icons/InsertDriveFileIcon';
import LocationCityIcon from '../Icons/LocationCityIcon';
import SaveAltIcon from '../Icons/SaveAltIcon';
import AssessoresContrastIcon from './../../assets/images/svg/menu-assessores-contrast-icon.svg';
import AssessoresIcon from './../../assets/images/svg/menu-assessores-icon.svg';
import CoordenadoresContrastIcon from './../../assets/images/svg/menu-coordenadores-contrast-icon.svg';
import CoordenadoresIcon from './../../assets/images/svg/menu-coordenadores-icon.svg';
import EscolasContrastIcon from './../../assets/images/svg/menu-escolas-contrast-icon.svg';
import EscolasIcon from './../../assets/images/svg/menu-escolas-icon.svg';
import ProfessoresContrastIcon from './../../assets/images/svg/menu-professores-contrast-icon.svg';
import ProfessoresIcon from './../../assets/images/svg/menu-professores-icon.svg';
import ProjetosContrastIcon from './../../assets/images/svg/menu-projetos-contrast-icon.svg';
import ProjetosIcon from './../../assets/images/svg/menu-projetos-icon.svg';
import {
  agendaAssessoresPedagogicosRoute,
  assessoresRoute,
  contratosRoute,
  cooperativasEscolares,
  coordenadoresRoute,
  dashboardRoute,
  empresasRoute,
  escolasRoute,
  jornadaEducacaoFinanceira,
  professoresRoute,
  projetosRoute,
  relatoriosRoute,
  uniaoFazAVidaRoute,
} from './../../library/Routes';
import styles from './LeftSidebar.scss';
import { toClose, toOpen } from './LeftSidebarActions';
import LeftSidebarMenuItem from './LeftSidebarMenuItem';
import { PROFILE_PUFV_OPERACIONAL } from '../../library/Profiles';
import CloseIconModal from 'assets/icons/CloseIconModal';
import { LogoProgramasEducacionais } from 'components/LogoProgramasEducacionais/LogoProgramasEducacionais';

const rootClassName = styles.rootClassName;
const rootElementId = 'left-sidebar';

const LeftSidebar = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const menuItemsRef = useRef(null);

  const languageValues = useSelector(state => state.languages.languageValues);
  const opened = useSelector(state => state.leftSidebar.opened);
  const auth = useSelector(state => state.auth);

  const getMenuItems = useCallback(() => {
    let arr = [];
    if (auth.userPermissions.projetos.list) {
      arr.push({
        normalIcon: ProjetosIcon,
        hoverIcon: ProjetosContrastIcon,
        label: languageValues.leftSideBar.projetos,
        path: projetosRoute.path,
        id: 'projetos',
      });
    }
    if (auth.userPermissions.professores.list) {
      arr.push({
        normalIcon: ProfessoresIcon,
        hoverIcon: ProfessoresContrastIcon,
        label: languageValues.leftSideBar.professores,
        path: professoresRoute.path,
        id: 'professores',
      });
    }
    if (auth.userPermissions.assessores.list) {
      arr.push({
        normalIcon: AssessoresIcon,
        hoverIcon: AssessoresContrastIcon,
        label: languageValues.leftSideBar.assessores,
        path: assessoresRoute.path,
        id: 'assessores',
      });
    }
    if (auth.userPermissions.coordenadores.list) {
      arr.push({
        normalIcon: CoordenadoresIcon,
        hoverIcon: CoordenadoresContrastIcon,
        label: languageValues.leftSideBar.coordenadores,
        path: coordenadoresRoute.path,
        id: 'coordenadores',
      });
    }
    if (auth.userPermissions.escolas.list) {
      arr.push({
        normalIcon: EscolasIcon,
        hoverIcon: EscolasContrastIcon,
        label: languageValues.leftSideBar.escolas,
        path: escolasRoute.path,
        id: 'escolas',
      });
    }
    if (auth.userPermissions.empresas.list) {
      arr.push({
        normalIcon: <LocationCityIcon />,
        hoverIcon: <LocationCityIcon />,
        label: languageValues.leftSideBar.empresas,
        path: empresasRoute.path,
        id: 'empresas',
      });
    }
    if (auth.userPermissions.contratos.list) {
      arr.push({
        normalIcon: <InsertDriveFileIcon />,
        hoverIcon: <InsertDriveFileIcon />,
        label: languageValues.leftSideBar.contratos,
        path: contratosRoute.path,
        id: 'contratos',
      });
    }
    if (
      auth.userPermissions.relatorios.generate.projetos ||
      auth.userPermissions.relatorios.generate['dados-cadastrais'] ||
      auth.userPermissions.relatorios.generate['dados-gerais'] ||
      auth.userPermissions.relatorios.generate['dados-do-programa'] ||
      auth.userPermissions.relatorios.generate['assessoria'] ||
      auth.userPermissions.relatorios.generate['contratos']
    ) {
      arr.push({
        normalIcon: <BarChartIcon />,
        hoverIcon: <BarChartIcon />,
        label: languageValues.leftSideBar.relatorios,
        path: relatoriosRoute.path,
        id: 'relatorios',
      });
    }
    if (auth.userPermissions.materiaisPedagogicos.list) {
      arr.push({
        normalIcon: <SaveAltIcon />,
        hoverIcon: <SaveAltIcon />,
        label: languageValues.leftSideBar.materiais,
        path: '',
        id: 'materiais-pedagogicos',
        subMenus: [
          {
            idSubMenu: 'uniao-faz-a-vida',
            labelSubMenu: languageValues.leftSideBar.pufv,
            pathSubMenu: uniaoFazAVidaRoute.path,
          },
          {
            idSubMenu: 'cooperativas-escolares',
            labelSubMenu: languageValues.leftSideBar.cooperativasEscolares,
            pathSubMenu: cooperativasEscolares.path,
          },
          {
            idSubMenu: 'jornada-educacao-financeira',
            labelSubMenu: languageValues.leftSideBar.jornadaEducacaoFinanceira,
            pathSubMenu: jornadaEducacaoFinanceira.path,
          },
        ],
      });
    }
    if (auth.userPermissions.agendaAssessoresPedagogicos.list) {
      arr.push({
        normalIcon: <CalendarTodayIcon />,
        hoverIcon: <CalendarTodayIcon />,
        label: languageValues.leftSideBar.agendaAssessoresPedagogicos,
        path: agendaAssessoresPedagogicosRoute.path,
        id: 'agenda-assessores-pedagogicos',
      });
    }
    if (auth.userPermissions.dashboard.generate) {
      arr.push({
        normalIcon: DashboardIcon,
        hoverIcon: DashboardContrastIcon,
        label: languageValues.leftSideBar.dashboard,
        path: dashboardRoute.path,
        id: 'dashboard',
      });
    }
    return arr;
  }, [auth, languageValues]);

  const toHomePage = () => {
    auth.userData.perfil === PROFILE_PUFV_OPERACIONAL.id
      ? history.push(contratosRoute.path)
      : history.push(projetosRoute.path);
  };

  const sidebarContent = () => (
    <div className={rootClassName + '-content'}>
      <div className={rootClassName + '-header'}>
        <button
          id={getElementID('left-sidebar', 'logo')}
          className={rootClassName + '-logotipo'}
          onClick={toHomePage}
        >
          <LogoProgramasEducacionais />
        </button>
        <button
          id={getElementID('left-sidebar', 'close')}
          className={rootClassName + '-close'}
          onClick={() => dispatch(toClose())}
        >
          <CloseIconModal />
        </button>
      </div>
      <div className={rootClassName + '-menu-items'} ref={menuItemsRef}>
        {getMenuItems().map((item, index) => (
          <LeftSidebarMenuItem
            path={item.path}
            id={getElementID(rootElementId, item.id)}
            key={index}
            normalIcon={item.normalIcon}
            hoverIcon={item.hoverIcon}
            label={item.label}
            subMenus={item.subMenus ?? []}
          />
        ))}
      </div>
    </div>
  );

  useEffect(() => {
    if (!opened) {
      setTimeout(() => {
        if (menuItemsRef.current) {
          menuItemsRef.current.scrollTop = 0;
        }
      }, 0);
    }
  }, [opened]);

  const onSetSidebarOpen = (open) => {
    if (open) {
      dispatch(toOpen());
    } else {
      dispatch(toClose());
    }
  };

  const sidebarProps = {
    sidebar: sidebarContent(),
    open: opened,
    onSetOpen: onSetSidebarOpen,
    sidebarClassName: rootClassName,
    overlayClassName: rootClassName + '-overlay',
  };

  return isAuthenticated() ? (
    <Sidebar {...sidebarProps}>
      <></>
    </Sidebar>
  ) : (
    ''
  );
};

export default LeftSidebar;